import React, { useEffect, useState, useContext } from "react";
import secureAxios from "../../utils/secureAxios";
import { AuthContext } from "../../contexts/AuthContext";

const CreateJobCoachAssignmentForm = ({ onSuccess }) => {
  const [formData, setFormData] = useState({
    consumer_id: "",
    user_id: "",
    start_date: "",
  });

  const [consumers, setConsumers] = useState([]);
  const [users, setUsers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { user } = useContext(AuthContext);
  const created_by = user?.user_id;
  const tenant_id = user?.tenant_id;

  useEffect(() => {
    if (!tenant_id || !user?.user_id || !user?.role || !user?.tenant_type) {
      return;
    }

    const controller = new AbortController();

    const fetchConsumers = async () => {
      try {
        const res = await secureAxios.get("/api/consumers", {
          params: {
            tenant_id,
            user_id: user.user_id,
            role: user.role,
            tenant_type: user.tenant_type,
          },
          signal: controller.signal,
        });
        setConsumers(res.data);
      } catch (err) {
        if (err.name !== "CanceledError") {
          console.error("Error fetching consumers:", err.message);
        }
      }
    };

    const fetchUsers = async () => {
      try {
        const res = await secureAxios.get("/api/users", {
          params: {
            tenant_id,
            role: "job_coach",
          },
          signal: controller.signal,
        });
        setUsers(res.data);
      } catch (err) {
        if (err.name !== "CanceledError") {
          console.error("Error fetching users:", err.message);
        }
      }
    };

    fetchConsumers();
    fetchUsers();

    return () => controller.abort();
  }, [tenant_id, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!created_by || !tenant_id) {
      alert("Missing user or tenant info. Please log in again.");
      return;
    }

    setIsSubmitting(true);

    const payload = {
      ...formData,
      tenant_id,
      created_by,
      created_by_name: user?.name,
      created_by_role: user?.role,
    };

    try {
      await secureAxios.post("/api/job-coach-assignments", payload);
      setFormData({ consumer_id: "", user_id: "", start_date: "" });
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Error creating assignment:", err.message);
      const errorMessage =
        err.response?.data?.error ||
        "An error occurred while creating the assignment.";
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-3">
        <label htmlFor="consumer-select" className="form-label">
          Consumer <span className="text-danger">*</span>
        </label>
        <select
          id="consumer-select"
          className="form-control"
          value={formData.consumer_id}
          onChange={(e) =>
            setFormData({ ...formData, consumer_id: e.target.value })
          }
          required
          disabled={isSubmitting}
        >
          <option value="">-- Select Consumer --</option>
          {consumers.map((c) => (
            <option key={c.consumer_id} value={c.consumer_id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-3">
        <label htmlFor="coach-select" className="form-label">
          Job Coach <span className="text-danger">*</span>
        </label>
        <select
          id="coach-select"
          className="form-control"
          value={formData.user_id}
          onChange={(e) =>
            setFormData({ ...formData, user_id: e.target.value })
          }
          required
          disabled={isSubmitting}
        >
          <option value="">-- Select Job Coach --</option>
          {users.map((u) => (
            <option key={u.user_id} value={u.user_id}>
              {u.name} ({u.email})
            </option>
          ))}
        </select>
      </div>

      <div className="mb-3">
        <label htmlFor="start-date" className="form-label">
          Start Date <span className="text-danger">*</span>
        </label>
        <input
          id="start-date"
          type="date"
          className="form-control"
          value={formData.start_date}
          onChange={(e) =>
            setFormData({ ...formData, start_date: e.target.value })
          }
          required
          disabled={isSubmitting}
        />
      </div>

      <button type="submit" className="btn btn-success" disabled={isSubmitting}>
        {isSubmitting ? "Assigning..." : "Assign Job Coach"}
      </button>
    </form>
  );
};

export default CreateJobCoachAssignmentForm;
